/**
 * useManpower.js - the calendar's shared state, held one level above both the
 * cards and the grid.
 *
 * This used to live inside ManpowerCalendar, which meant the engineer cards
 * had no way of knowing what the calendar said. The Engineers tab calls this
 * once and hands the result to both: put someone on a project in the calendar
 * and their card changes, set a project on the card and the calendar fills in.
 * One store, two views of it.
 *
 * Shape:
 *   edits          { 'EngineerName|YYYY-MM-DD': 'Project' | null }
 *   details        { 'EngineerName|YYYY-MM-DD': { location?, projectNumber?, status? } }
 *                  per scheduled day, never per label: the same project can be
 *                  On-Site one week and Office the next
 *   customProjects [ 'Project', ... ]  labels the user typed
 *   colors         { 'Project': '#hex' }
 *
 * All four are shared by everyone — they live in Postgres behind
 * /api/manpower/edits. localStorage is only an offline cache: the last copy
 * shows at once while the server's is on its way. It does not carry unsaved
 * changes over a page reload - the server's copy replaces it.
 *
 * Saving (r50). A save used to PUT this tab's whole copy, which the server
 * takes as the entire schedule: two people saving a second apart kept only the
 * later save, and a tab whose save had failed would, on its next save, delete
 * everything saved elsewhere since. Now the tab notes what it changed since its
 * last successful save (`changes`, below) and PATCHes only that - the days it
 * touched, the label names it added or removed and the colours it changed,
 * never a whole list (see buildSave). A failed save keeps its changes and
 * tries again (2 s, 4 s ... every 60 s) until it goes through, the calendar
 * closed or not; the next calendar opened in this tab takes those changes
 * over (closedWithChanges, below), so leaving the Engineers tab while the
 * server is unreachable does not drop them.
 * Someone else's save (a live event) is always re-read at once: the server's
 * copy, with this tab's unsaved changes put back on top.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { LS_COLOR_KEY, LS_CUSTOM_PROJ_KEY, LS_EDITS_KEY } from './manpowerConstants';
import { useLiveRefresh } from '../../live/useLive';

import {
  LS_DETAILS_KEY,
  readLS,
  writeLS,
  SAVE_DEBOUNCE_MS,
  pruneDetails,
  noChanges,
  markDays,
  markNames,
  markColors,
  unionChanges,
  rebaseNames,
  rebaseColors,
  hasChanges,
  copyChanges,
} from './useManpower/scheduleChanges';
import { openCalendars, closedWithChanges } from './useManpower/openCalendars';
import { useManpowerSync } from './useManpower/useManpowerSync';
import { useManpowerEdits } from './useManpower/useManpowerEdits';
export {
  RETRY_FIRST_MS,
  RETRY_MAX_MS,
  nextRetryMs,
  moveDays,
  cleanDetail,
  moveDetails,
  pruneDetails,
  noChanges,
  sameDetail,
  markDays,
  markNames,
  markColors,
  unionChanges,
  rebaseDays,
  rebaseNames,
  rebaseColors,
  buildSave,
  settle,
} from './useManpower/scheduleChanges';
export { forgetClosedCalendars } from './useManpower/openCalendars';

export function useManpower({ onSaved } = {}) {
  const [palette, setPaletteState] = useState(() => readLS(LS_COLOR_KEY, '{}'));
  // Labels and details in ONE state: every edit changes both, and a details
  // update worked out from a stale labels map would put a location on the
  // wrong days, or keep one on a day that was just cleared.
  const [sched, setSchedState] = useState(() => ({
    edits: readLS(LS_EDITS_KEY, '{}'),
    details: readLS(LS_DETAILS_KEY, '{}'),
  }));
  const { edits, details } = sched;
  const [customProjects, setCustomProjectsState] = useState(() => readLS(LS_CUSTOM_PROJ_KEY, '[]'));
  // 'idle' | 'loading' | 'saving' | 'error' | 'reload'. 'reload': the server
  // answered a save 409 reload_required - this page is older than the
  // server's rules and only a page reload can save. It is still retried like
  // any failed save, in case that clears.
  const [syncStatus, setSyncStatus] = useState('idle');
  // True once the server copy has arrived. Unlike syncStatus it never goes back:
  // a failed SAVE also reads 'error', but the schedule on screen is still the real one.
  const [loaded, setLoaded] = useState(false);

  // Latest values in a ref so a save always reads the freshest state - even
  // if two setters fire back-to-back inside a single user action (e.g.
  // applyEdit: writeDays + addCustomProject).
  const latestRef = useRef({ edits, details, customProjects, colors: palette });
  useEffect(() => {
    latestRef.current = { edits, details, customProjects, colors: palette };
  }, [edits, details, customProjects, palette]);

  // In a ref so a save always calls the caller's latest handler.
  const onSavedRef = useRef(onSaved);
  useEffect(() => {
    onSavedRef.current = onSaved;
  }, [onSaved]);

  // What this tab changed since its last successful save (see noChanges):
  // all a save sends, and what a re-read puts back on top of the server's copy.
  const changesRef = useRef(noChanges());

  // Nothing goes up before the server's copy has arrived: a change made on the
  // offline cache is sent only once it sits on top of the real schedule.
  const loadedRef = useRef(false);
  // A load that came back without any details (a server older than this page)
  // leaves `details` out of the save, so the server keeps what it has.
  const detailsLoadedRef = useRef(false);

  const aliveRef = useRef(true); // false once unmounted (the calendar closed)
  const busyRef = useRef(false); // a save is running; one at a time, so ours land in order
  const timerRef = useRef(null); // the next save, debounced or a retry
  const timerIsRetryRef = useRef(false);
  const retryMsRef = useRef(0); // the last wait after a failure; 0 = nothing failing
  const saveFailedRef = useRef(false); // the last PATCH failed: its status ('error' | 'reload') stays up until one works
  const readSeqRef = useRef(0); // re-reads started
  const shownSeqRef = useRef(0); // the newest re-read now on screen
  const landedRef = useRef(0); // saves of ours that went through
  const rereadRef = useRef(false); // a re-read gave up: read again after the next save
  const syncRef = useRef(null); // sync(), set below
  const reloadRef = useRef(null); // reloadFromServer(), set below
  const retiredRef = useRef(false); // closed, its changes taken over by a newer calendar: sends nothing more

  // This calendar's entry in closedWithChanges, for when it closes with
  // changes unsaved. takeOver() hands them to a newer calendar - as they are
  // then, values included - and stops this one.
  const closedRef = useRef(null);
  if (!closedRef.current) {
    closedRef.current = {
      takeOver: () => {
        retiredRef.current = true;
        closedWithChanges.delete(closedRef.current);
        clearTimeout(timerRef.current);
        timerRef.current = null;
        return { changes: copyChanges(changesRef.current), local: latestRef.current };
      },
    };
  }

  const schedule = useCallback((ms, retry = false) => {
    clearTimeout(timerRef.current);
    timerIsRetryRef.current = retry;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (syncRef.current) syncRef.current();
    }, ms);
  }, []);

  const setPalette = useCallback(
    (updater) => {
      setPaletteState((cur) => {
        const next = typeof updater === 'function' ? updater(cur) : updater;
        if (next === cur) return cur;
        writeLS(LS_COLOR_KEY, next);
        latestRef.current = { ...latestRef.current, colors: next };
        if (markColors(changesRef.current, cur, next)) schedule(SAVE_DEBOUNCE_MS);
        return next;
      });
    },
    [schedule]
  );

  // The one writer for labels + details together: `updater` takes and
  // returns { edits, details }.
  const setSchedule = useCallback(
    (updater) => {
      setSchedState((cur) => {
        const next = updater(cur);
        if (next === cur || (next.edits === cur.edits && next.details === cur.details)) return cur;
        writeLS(LS_EDITS_KEY, next.edits);
        writeLS(LS_DETAILS_KEY, next.details);
        latestRef.current = { ...latestRef.current, edits: next.edits, details: next.details };
        if (markDays(changesRef.current.days, cur, next)) schedule(SAVE_DEBOUNCE_MS);
        return next;
      });
    },
    [schedule]
  );

  // Labels only. A day it empties drops its details too.
  const setEdits = useCallback(
    (updater) => {
      setSchedule((cur) => {
        const nextEdits = typeof updater === 'function' ? updater(cur.edits) : updater;
        return { edits: nextEdits, details: pruneDetails(cur.details, nextEdits) };
      });
    },
    [setSchedule]
  );

  const setCustomProjects = useCallback(
    (updater) => {
      setCustomProjectsState((cur) => {
        const next = typeof updater === 'function' ? updater(cur) : updater;
        if (next === cur) return cur;
        writeLS(LS_CUSTOM_PROJ_KEY, next);
        latestRef.current = { ...latestRef.current, customProjects: next };
        if (markNames(changesRef.current, cur, next)) schedule(SAVE_DEBOUNCE_MS);
        return next;
      });
    },
    [schedule]
  );

  const { sync, readServer } = useManpowerSync({
    changesRef,
    setSchedState,
    latestRef,
    setCustomProjectsState,
    setPaletteState,
    detailsLoadedRef,
    loadedRef,
    setLoaded,
    aliveRef,
    readSeqRef,
    landedRef,
    shownSeqRef,
    rereadRef,
    setSyncStatus,
    retiredRef,
    retryMsRef,
    schedule,
    busyRef,
    saveFailedRef,
    onSavedRef,
    reloadRef,
    closedRef,
    timerRef,
    syncRef,
  });
  useEffect(() => {
    syncRef.current = sync;
  }, [sync]);

  // A closed calendar's unsaved changes made this one's: noted as changed, and
  // its values for them put over what this one shows - each day whole, label
  // and details, whatever the offline cache (other browser tabs write it too)
  // says now. So they show at once and go up with this calendar's own, on the
  // server's copy once that is in.
  const adopt = useCallback(({ changes, local }) => {
    changesRef.current = unionChanges(changesRef.current, changes);
    const cur = latestRef.current;
    const edits = { ...cur.edits };
    const details = { ...cur.details };
    for (const k of changes.days.keys()) {
      if (local.edits[k] == null) delete edits[k];
      else edits[k] = local.edits[k];
      if (local.details[k] == null) delete details[k];
      else details[k] = local.details[k];
    }
    const customProjects = rebaseNames(cur.customProjects, local.customProjects, changes);
    const colors = rebaseColors(cur.colors, local.colors, changes);
    latestRef.current = { edits, details, customProjects, colors };
    writeLS(LS_EDITS_KEY, edits);
    writeLS(LS_DETAILS_KEY, details);
    writeLS(LS_CUSTOM_PROJ_KEY, customProjects);
    writeLS(LS_COLOR_KEY, colors);
    setSchedState({ edits, details });
    setCustomProjectsState(customProjects);
    setPaletteState(colors);
  }, []);

  // On mount: take over whatever calendars closed in this tab could not save,
  // then pull the shared state (the offline cache shows until it comes).
  // On unmount a save still waiting goes up at once - it holds a change
  // someone made - and a retry after a failure carries on (see failed); what
  // is still unsaved waits in closedWithChanges for the next calendar.
  useEffect(() => {
    aliveRef.current = true;
    for (const c of [...closedWithChanges]) {
      if (c === closedRef.current)
        closedWithChanges.delete(c); // this one, mounted again
      else adopt(c.takeOver());
    }
    setSyncStatus('loading');
    if (syncRef.current) syncRef.current();
    return () => {
      aliveRef.current = false;
      if (timerRef.current && !timerIsRetryRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
        if (syncRef.current) syncRef.current();
      }
      if (hasChanges(changesRef.current)) closedWithChanges.add(closedRef.current);
    };
  }, [adopt]);

  // Someone else's calendar save (a live event, or a resync after the live
  // connection dropped): re-read at once, even with a save of ours waiting or
  // failing - ours go back on top. A failed re-read keeps what is on screen.
  const reloadFromServer = useCallback(async () => {
    const was = loadedRef.current;
    try {
      if (!(await readServer())) return;
    } catch {
      return; /* offline: the next change or a resync catches up */
    }
    if (was || !aliveRef.current) return;
    // the first copy to arrive: anything typed meanwhile can go up now
    retryMsRef.current = 0;
    if (hasChanges(changesRef.current)) schedule(SAVE_DEBOUNCE_MS);
    else if (!busyRef.current) setSyncStatus('idle');
  }, [readServer, schedule]);
  useEffect(() => {
    reloadRef.current = reloadFromServer;
  }, [reloadFromServer]);
  useLiveRefresh('manpower', reloadFromServer);
  useEffect(() => {
    openCalendars.add(reloadFromServer);
    return () => {
      openCalendars.delete(reloadFromServer);
    };
  }, [reloadFromServer]);

  const {
    setPaletteOverride,
    colorFor,
    addCustomProject,
    deleteCustomProject,
    applyEdit,
    moveEdit,
    fillProjectNumber,
    projectOn,
    detailOn,
  } = useManpowerEdits({ setCustomProjects, setEdits, setPalette, palette, setSchedule, edits, details });

  return {
    edits,
    details,
    customProjects,
    palette,
    syncStatus,
    loaded,
    setEdits,
    setPaletteOverride,
    colorFor,
    addCustomProject,
    deleteCustomProject,
    applyEdit,
    moveEdit,
    fillProjectNumber,
    projectOn,
    detailOn,
  };
}
